import { useEffect, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { executionApi, ExecutionApiError } from './api';
import type { ExecutionObjective, ObjectiveCheckIn, OwnerHealth, PrimaryMeasure } from './model';
import {
  describeError,
  formatExecutionDate,
  Modal,
  OutcomeProgress,
  useExecutionActive,
} from './ExecutionShared';

export type TrackingTab = 'checkin' | 'measure' | 'history';
function localDateTime() {
  const date = new Date();
  const two = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}T${two(date.getHours())}:${two(date.getMinutes())}`;
}
export default function ObjectiveTrackingDialog({
  objective,
  companyId,
  canEdit,
  initialTab,
  onClose,
  onSaved,
}: {
  objective: ExecutionObjective;
  companyId: string;
  canEdit: boolean;
  initialTab: TrackingTab;
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
}) {
  const active = useExecutionActive();
  const [tab, setTab] = useState<TrackingTab>(initialTab);
  const [kind, setKind] = useState<'numeric' | 'milestone'>(
    objective.primaryMeasure?.kind ?? 'numeric'
  );
  const [label, setLabel] = useState(
    objective.primaryMeasure?.label ?? objective.measureLabel ?? ''
  );
  const numeric = objective.primaryMeasure?.kind === 'numeric' ? objective.primaryMeasure : null;
  const [unit, setUnit] = useState(numeric?.unit ?? '');
  const [baseline, setBaseline] = useState(numeric ? String(numeric.baseline) : '');
  const [target, setTarget] = useState(numeric ? String(numeric.target) : '');
  const [baselineDate, setBaselineDate] = useState(numeric?.baselineDate?.slice(0, 10) ?? '');
  const [interval, setInterval] = useState(objective.reviewIntervalDays ?? 14);
  const [observedAt, setObservedAt] = useState(localDateTime);
  const [value, setValue] = useState('');
  const [milestone, setMilestone] = useState('');
  const [health, setHealth] = useState('');
  const [note, setNote] = useState('');
  const [history, setHistory] = useState<ObjectiveCheckIn[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyReload, setHistoryReload] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  useEffect(() => {
    if (tab !== 'history') return;
    let active = true;
    setHistory(null);
    setHistoryError(null);
    executionApi
      .checkIns(companyId, objective.id)
      .then((result) => {
        if (active) setHistory(result);
      })
      .catch((err) => {
        if (active) setHistoryError(describeError(err));
      });
    return () => {
      active = false;
    };
  }, [tab, companyId, objective.id, historyReload]);
  async function saveMeasure(event: FormEvent) {
    event.preventDefault();
    if (saving || !canEdit || conflict) return;
    setError(null);
    if (kind === 'numeric' && Number(baseline) === Number(target)) {
      setError('Baseline and target must differ so progress can be measured.');
      return;
    }
    const measure: PrimaryMeasure =
      kind === 'milestone'
        ? { kind, label: label.trim() }
        : {
            kind,
            label: label.trim(),
            unit: unit.trim(),
            baseline: Number(baseline),
            target: Number(target),
            baselineDate: baselineDate || null,
          };
    setSaving(true);
    try {
      await executionApi.saveMeasure(
        companyId,
        objective.id,
        measure,
        objective.measureRevision,
        interval
      );
      if (!active.current) return;
      await onSaved('Objective tracking saved. Previous check-ins remain in history.');
      if (active.current) onClose();
    } catch (err) {
      if (!active.current) return;
      setError(describeError(err));
      if (err instanceof ExecutionApiError && err.status === 409) setConflict(true);
    } finally {
      if (active.current) setSaving(false);
    }
  }
  async function saveCheckIn(event: FormEvent) {
    event.preventDefault();
    if (saving || !canEdit || conflict) return;
    setError(null);
    if (!value && !milestone && !health && !note.trim()) {
      setError('Add an outcome reading, owner assessment or evidence note.');
      return;
    }
    setSaving(true);
    try {
      await executionApi.checkIn(companyId, objective.id, {
        expectedRevision: objective.measureRevision,
        observedAt: new Date(observedAt).toISOString(),
        ...(value !== '' && objective.primaryMeasure?.kind === 'numeric'
          ? { value: Number(value) }
          : {}),
        ...(milestone !== '' && objective.primaryMeasure?.kind === 'milestone'
          ? { milestoneAchieved: milestone === 'yes' }
          : {}),
        ...(health ? { ownerHealth: health as OwnerHealth } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      if (!active.current) return;
      await onSaved('Outcome check-in recorded.');
      if (active.current) onClose();
    } catch (err) {
      if (!active.current) return;
      setError(describeError(err));
      if (err instanceof ExecutionApiError && err.status === 409) setConflict(true);
    } finally {
      if (active.current) setSaving(false);
    }
  }
  return (
    <Modal title="Objective tracking" description={objective.title} onClose={onClose}>
      <div
        className="execution-view-tabs"
        role="group"
        aria-label="Objective tracking sections"
        style={{ marginBottom: 22 }}
      >
        {[
          ['checkin', 'Check-in'],
          ['measure', 'Primary measure'],
          ['history', 'History'],
        ].map(([id, text]) => (
          <button
            key={id}
            type="button"
            aria-pressed={tab === id}
            onClick={() => {
              setTab(id as TrackingTab);
              setError(null);
            }}
          >
            {text}
          </button>
        ))}
      </div>
      {tab === 'checkin' && (
        <form onSubmit={saveCheckIn}>
          <div style={{ marginBottom: 20 }}>
            <OutcomeProgress objective={objective} detailed />
          </div>
          {!objective.primaryMeasure && (
            <div className="execution-notice">
              Set a primary measure to record outcome values. You can record an owner assessment or
              evidence note now.
            </div>
          )}
          <label htmlFor="execution-checkin-date">When was this observed?</label>
          <input
            id="execution-checkin-date"
            type="datetime-local"
            value={observedAt}
            onChange={(e) => setObservedAt(e.target.value)}
            required
          />
          {objective.primaryMeasure?.kind === 'numeric' && (
            <>
              <label htmlFor="execution-checkin-value">
                {objective.primaryMeasure.label}
                {objective.primaryMeasure.unit ? ` (${objective.primaryMeasure.unit})` : ''}
              </label>
              <input
                id="execution-checkin-value"
                type="number"
                step="any"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter the actual reading"
              />
            </>
          )}
          {objective.primaryMeasure?.kind === 'milestone' && (
            <>
              <label htmlFor="execution-checkin-milestone">{objective.primaryMeasure.label}</label>
              <select
                id="execution-checkin-milestone"
                value={milestone}
                onChange={(e) => setMilestone(e.target.value)}
              >
                <option value="">No milestone update</option>
                <option value="no">Not achieved yet</option>
                <option value="yes">Achieved</option>
              </select>
            </>
          )}
          <label htmlFor="execution-checkin-health">Owner’s assessment</label>
          <select
            id="execution-checkin-health"
            value={health}
            onChange={(e) => setHealth(e.target.value)}
          >
            <option value="">No assessment update</option>
            <option value="on_track">On track</option>
            <option value="at_risk">At risk</option>
            <option value="off_track">Off track</option>
          </select>
          <p className="execution-dialog-help">
            Owner confidence is shown separately from measured outcome progress and calculated pace.
          </p>
          <label htmlFor="execution-checkin-note">Evidence, blockers and what happens next</label>
          <textarea
            id="execution-checkin-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={20000}
            placeholder="What changed? What supports this reading? What will move the needle next?"
          />
          {error && (
            <div className="execution-notice error" role="alert">
              {error}
              {conflict &&
                ' Your inputs are retained. Close this dialog, refresh the workspace and review the current measure before retrying.'}
            </div>
          )}
          <div className="execution-dialog-footer">
            <button className="execution-button" type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              className="execution-button primary"
              type="submit"
              disabled={saving || !canEdit || conflict}
            >
              {saving ? 'Recording…' : 'Record check-in'}
            </button>
          </div>
        </form>
      )}
      {tab === 'measure' && (
        <form onSubmit={saveMeasure}>
          <label htmlFor="execution-measure-kind">How will success be measured?</label>
          <select
            id="execution-measure-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'numeric' | 'milestone')}
          >
            <option value="numeric">Numeric outcome</option>
            <option value="milestone">Achieved milestone</option>
          </select>
          <label htmlFor="execution-measure-label">Primary measure</label>
          <input
            id="execution-measure-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={500}
            placeholder={
              kind === 'numeric'
                ? 'For example, recurring revenue'
                : 'For example, launch the new service'
            }
          />
          {kind === 'numeric' && (
            <>
              <label htmlFor="execution-measure-unit">Unit</label>
              <input
                id="execution-measure-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                maxLength={100}
                placeholder="$, customers, %, hours…"
              />
              <div className="execution-dialog-grid">
                <div>
                  <label htmlFor="execution-measure-baseline">Baseline</label>
                  <input
                    id="execution-measure-baseline"
                    type="number"
                    step="any"
                    value={baseline}
                    onChange={(e) => setBaseline(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="execution-measure-target">Target</label>
                  <input
                    id="execution-measure-target"
                    type="number"
                    step="any"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    required
                  />
                </div>
              </div>
              <label htmlFor="execution-measure-baseline-date">Baseline date</label>
              <input
                id="execution-measure-baseline-date"
                type="date"
                value={baselineDate}
                onChange={(e) => setBaselineDate(e.target.value)}
              />
              <p className="execution-dialog-help">
                Use a lower target for a measure you want to reduce. Pace is available when baseline
                date and objective target date are set.
              </p>
            </>
          )}
          <label htmlFor="execution-measure-cadence">Review every (days)</label>
          <input
            id="execution-measure-cadence"
            type="number"
            min={1}
            max={365}
            value={interval}
            onChange={(e) => setInterval(Number(e.target.value))}
            required
          />
          <p className="execution-dialog-help">
            Changing the measure creates a new revision. Existing check-ins remain attached to their
            original measure and target date.
          </p>
          {error && (
            <div className="execution-notice error" role="alert">
              {error}
              {conflict &&
                ' Your inputs are retained. Close this dialog, refresh the workspace and review the current measure before retrying.'}
            </div>
          )}
          <div className="execution-dialog-footer">
            <button className="execution-button" type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              className="execution-button primary"
              type="submit"
              disabled={saving || !canEdit || conflict}
            >
              {saving ? 'Saving…' : 'Save tracking'}
            </button>
          </div>
        </form>
      )}
      {tab === 'history' && (
        <div className="execution-history" style={{ borderTop: 0, marginTop: 0, paddingTop: 0 }}>
          <h3>Outcome check-in history</h3>
          {historyError ? (
            <div className="execution-notice error" role="alert">
              <span>{historyError}</span>
              <button type="button" onClick={() => setHistoryReload((value) => value + 1)}>
                Retry
              </button>
            </div>
          ) : history === null ? (
            <p className="execution-dialog-help">
              <Loader2 size={14} className="animate-spin" /> Loading history…
            </p>
          ) : history.length === 0 ? (
            <p className="execution-dialog-help">
              No check-ins recorded yet. Record the first reading to establish progress.
            </p>
          ) : (
            history.map((checkIn) => (
              <div className="execution-history-row" key={checkIn.id}>
                <strong>
                  {checkIn.value !== null
                    ? checkIn.value.toLocaleString()
                    : checkIn.milestoneAchieved !== null
                      ? checkIn.milestoneAchieved
                        ? 'Milestone achieved'
                        : 'Milestone not achieved'
                      : 'Owner update'}
                  {checkIn.measureSnapshot?.kind === 'numeric' && checkIn.value !== null
                    ? ` ${checkIn.measureSnapshot.unit}`
                    : ''}
                </strong>
                <small>{formatExecutionDate(checkIn.observedAt, true)}</small>
                <p>
                  {checkIn.measureSnapshot?.label ?? 'No measure'} · Revision{' '}
                  {checkIn.measureRevision}
                  {checkIn.measureRevision !== objective.measureRevision
                    ? ' (previous measure)'
                    : ''}{' '}
                  · Target {formatExecutionDate(checkIn.deadlineSnapshot, true)}
                </p>
                <p>
                  {checkIn.authorName}
                  {checkIn.ownerHealth
                    ? ` · Owner assessment: ${checkIn.ownerHealth.replace(/_/g, ' ')}`
                    : ''}
                </p>
                {checkIn.note && <p>{checkIn.note}</p>}
              </div>
            ))
          )}
          <div className="execution-dialog-footer">
            <button className="execution-button" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
